import { useEffect, useState } from 'react';
import { safeGet, safeSet, safeRemove } from '../utils/safeStorage';

/**
 * Debug mode — feature flag local pra expor controles de QA.
 *
 * Ativação:
 *  - Abrir o app com `?debug=1` na URL (uma vez basta)
 *  - A partir daí fica ativo nesse device até desligar
 *
 * Desativação:
 *  - Abrir com `?debug=0` na URL
 *  - Ou via DebugPanel ("Sair do modo debug")
 *
 * Quando ativo:
 *  - Aparece DebugPanel dentro do SettingsModal
 *  - Outros componentes podem usar `useDebugMode()` pra renderizar
 *    UI extra (ex: contador de re-renders, log overlay, etc)
 *
 * NÃO usar pra gating de features de produção — isso é só pra QA/teste.
 * Nada que afete usuários finais deveria depender desse flag.
 */

const STORAGE_KEY = '@sixpass:debug-mode';

/** Inicializa o flag baseado no URL param. Roda 1x no boot do app. */
export function initDebugMode(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const debugParam = url.searchParams.get('debug');

  if (debugParam === '1') {
    safeSet(STORAGE_KEY, '1');
    // Remove o param da URL pra não poluir links compartilhados.
    url.searchParams.delete('debug');
    window.history.replaceState({}, '', url.toString());
  } else if (debugParam === '0') {
    safeRemove(STORAGE_KEY);
    url.searchParams.delete('debug');
    window.history.replaceState({}, '', url.toString());
  }
}

export function isDebugMode(): boolean {
  return safeGet(STORAGE_KEY) === '1';
}

export function setDebugMode(enabled: boolean): void {
  if (enabled) safeSet(STORAGE_KEY, '1');
  else safeRemove(STORAGE_KEY);
}

/** Hook reativo. Retorna `true` se debug tá ligado nesse device. */
export function useDebugMode(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => isDebugMode());

  useEffect(() => {
    // Listener pra mudança de storage em outras abas (raro, mas correto).
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setEnabled(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return enabled;
}
